import { onMounted, onUnmounted, type Ref } from 'vue'

export interface ScrollAnimationOptions {
  threshold?: number // 元素可见多少比例时触发（0-1）
  rootMargin?: string // 提前触发的边距
  once?: boolean // 是否只触发一次
}

export function useScrollAnimation(
  elementRef: Ref<HTMLElement | null>,
  animationClass: string,
  options: ScrollAnimationOptions = {}
) {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -100px 0px',
    once = false,
  } = options

  let observer: IntersectionObserver | null = null

  onMounted(() => {
    if (!elementRef.value) return

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 元素进入视口，添加动画类
            entry.target.classList.add(animationClass)
          } else if (!once) {
            // 元素离开视口，且非一次性动画，移除动画类以便重新触发
            entry.target.classList.remove(animationClass)
          }
        })
      },
      {
        threshold,
        rootMargin,
      }
    )

    observer.observe(elementRef.value)
  })

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
    }
  })
}

// 批量绑定多个元素的滚动动画
export function useScrollAnimationBatch(
  elementsRef: Ref<HTMLElement[]>,
  animationClass: string,
  options: ScrollAnimationOptions = {}
) {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -100px 0px',
    once = false,
  } = options

  let observer: IntersectionObserver | null = null

  onMounted(() => {
    if (!elementsRef.value || elementsRef.value.length === 0) return

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(animationClass)
          } else if (!once) {
            entry.target.classList.remove(animationClass)
          }
        })
      },
      {
        threshold,
        rootMargin,
      }
    )

    elementsRef.value.forEach((el) => {
      if (el) observer?.observe(el)
    })
  })

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
    }
  })
}
